import {
	Breakpoints,
	Layout,
	styled,
	Text,
	ThemeProvider,
} from '@volkswagen-onehub/components-core';
import { GfaDealerContextServiceV1 } from '@volkswagen-onehub/gfa-dealer-context-service';
import {
	ArrowRight,
	Calc,
	Calendar,
	FindCar,
	Info,
	Interior,
} from '@volkswagen-onehub/icons-core';
import { UserInteractionServiceV1 } from '@volkswagen-onehub/user-interaction-service';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TriggerProps } from 'src/feature-app';
import {
	actionRedirect,
	LeadForm,
	useFaContent,
	useFeatureServices,
} from 'src/feature-app/hooks';
import {
	CitaVentaForm,
	MasInformacionForm,
	PresupuestoForm,
	StockForm,
	TestDriveForm,
	VOForm,
} from 'src/forms';
import { TRANSITION_TIME } from 'src/globals';

const Grid = styled.div`
	display: grid;
	grid-auto-rows: 1fr;
	grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
	gap: 12px;
	-webkit-box-pack: center;
	justify-content: center;
	text-size-adjust: 100%;
	line-height: 1.15;
	box-sizing: border-box;
	cursor: pointer;
`;
const Box = styled.div`
	display: grid;
	text-decoration: none;
	color: inherit;
	outline: none;
`;
const Content = styled.div`
	display: grid;
	grid-template-columns: 80% 20%;
	min-width: 220px;
	padding: 12px;
	border-radius: 8px;
	background-color: rgb(255, 255, 255);
	box-shadow: rgb(194, 202, 207) 0px 0px 0px 1px inset;
	transition: box-shadow ${TRANSITION_TIME.FAST}ms;

	& > svg:first-of-type {
		margin-bottom: 12px;
		grid-column: span 2;
	}

	& > div {
		grid-area: 2 / 1;
		align-self: end;
	}

	& > svg:last-of-type {
		grid-area: 2 / 2;
		place-self: end;
	}

	&:hover > svg:last-of-type {
		animation: arrowAnimation ${TRANSITION_TIME.FAST}ms ease-out;
	}

	@keyframes arrowAnimation {
		0% {
			transform: translateX(0%);
		}
		50% {
			transform: translateX(-35%);
		}
		100% {
			transform: translateX(0%);
		}
	}
`;

export function MyDealerTrigger({
	setSelectedJourney,
	setCTALabel,
}: TriggerProps) {
	const faContent = useFaContent();
	const dispatch = useDispatch();
	const [ctas, setCtas] = useState([]);

	const myDealerService = useFeatureServices()[
		'mydealer-service'
	] as unknown as GfaDealerContextServiceV1;
	const selectedDealer = myDealerService?.getSelectedDealer();
	const idDealer = selectedDealer?.id;

	const userInteractionService = useFeatureServices()[
		'user-interaction-service'
	] as UserInteractionServiceV1;

	const handleLayerOpen = (journey: JSX.Element, label: string) => {
		dispatch({ type: 'RESET_STATE' });
		setCTALabel(label);
		setSelectedJourney(journey);
	};

	const getCtaLabel = (leadType: string, defaultLabel: string) => {
		if (faContent?.ctaForms) {
			const ctaForm = faContent.ctaForms.find(
				el => el.leadType == leadType,
			);
			return ctaForm?.ctaLabel ? ctaForm.ctaLabel : defaultLabel;
		}
		return defaultLabel;
	};

	useEffect(() => {
		if (faContent?.ctaForms) {
			const sortedCtas = [
				{
					id: 'lead-test_drive',
					label: getCtaLabel(
						'lead-test_drive',
						'Probar un coche nuevo',
					),
					form: <TestDriveForm />,
					icon: <Interior />,
				},
				{
					id: 'lead-mas_informacion',
					label: getCtaLabel(
						'lead-mas_informacion',
						'Información de un coche nuevo',
					),
					form: <MasInformacionForm />,
					icon: <Info />,
				},

				{
					id: 'lead-presupuesto',
					label: getCtaLabel(
						'lead-presupuesto',
						'Presupuesto de un coche nuevo',
					),
					form: <PresupuestoForm />,
					icon: <Calc />,
				},
				{
					id: 'lead-vo',
					label: getCtaLabel(
						'lead-vo',
						'Información de un coche de ocasión',
					),
					form: <VOForm />,
					icon: <FindCar />,
				},
				{
					id: 'lead-stock',
					label: getCtaLabel(
						'lead-stock',
						'Buscar un coche nuevo en stock',
					),
					form: <StockForm />,
					icon: <FindCar />,
				},
				{
					id: 'lead-cita',
					label: getCtaLabel('lead-cita', 'Cita con un asesor'),
					form: <CitaVentaForm />,
					icon: <Calendar />,
				},
				{
					id: 'cita_posventa',
					label: getCtaLabel('cita_posventa', 'Cita en el taller'),
					form: null,
					icon: <Calendar />,
				},
			];

			const formTypes = faContent.ctaForms.map((obj: any) =>
				typeof obj == 'string' ? obj : obj.leadType,
			);
			const selectedCtas = sortedCtas.filter(cta =>
				formTypes.includes(cta.id as LeadForm),
			);
			setCtas(selectedCtas);
		}
	}, []);

	return (
		<ThemeProvider theme={'main'}>
			<Layout
				appearance={{
					[Breakpoints.default]: [
						{ name: '.', columns: 2 },
						{ name: 'a', columns: 20 },
						{ name: '.', columns: 2 },
					],
				}}
			>
				<Grid>
					{ctas.map(cta => (
						<Box
							key={cta.id}
							onClick={() => {
								if (cta.id == 'cita_posventa') {
									actionRedirect(
										userInteractionService,
										'action:es-cca-loader--open_layer' +
											(idDealer
												? `?dealer=${idDealer}`
												: ''),
									);
								} else {
									handleLayerOpen(cta.form, cta.label);
								}
							}}
						>
							<Content>
								{cta.icon}
								<Text bold>{cta.label}</Text>
								<ArrowRight />
							</Content>
						</Box>
					))}
				</Grid>
			</Layout>
		</ThemeProvider>
	);
}
